// migrateAfe — read-time normalization of legacy AFE register records (RM-14 / AFE S1).
//
// The pre-RM-14 `Afe` was capture-only with a free-text `afeStatus`. RM-14 adds a typed `status`
// (AfeStatus), a `currency` (Abs-3), and the `afeClass` work-kind axis. Existing `oxbo-afes-v1`
// records lack these. This PURE, IDEMPOTENT function fills them on read — it never writes; consumers
// see the normalized shape. (The normalized shape is only persisted the NEXT time the record is
// mutated via useAfeData create/update/delete, which is lossless: legacy `afeStatus` is preserved.)
//   - currency  ← existing value, else 'USD' (no bare/unitless money — Abs-3).
//   - status    ← existing typed `status`, else the legacy free-text `afeStatus` mapped to the
//                 lightweight enum (draft|submitted|approved; anything else → 'draft', never
//                 'approved' — a money-critical status is not inferred as authorized).
//   - afeClass  ← LEFT UNSET (unclassified-until-set — PO 2026-07-24: no forced class on migration).
//   - name      ← existing value, else the issued `afeNumber`, else a stable placeholder. PO
//                 2026-08-24 split the engineer's working TITLE from the accounting-issued NUMBER;
//                 every pre-split record has a number and no name, so the number seeds the name.
//                 This is a rename-on-read, NOT a fabricated identifier: it reuses the string the
//                 record already carried. `afeNumber` is left in place — a legacy record's number
//                 WAS issued, so it is not cleared.
//
// No engineering/economic/statistical math (Charter Abs-2) — field defaulting + a string map.

import type { Afe, AfeStatus } from "./types";

/** Map the legacy free-text `afeStatus` onto the typed lightweight AfeStatus. Unknown/blank text is
 *  conservatively 'draft' (never 'approved' — do not infer authorization from free text). */
export function mapLegacyAfeStatus(
  raw: string | undefined,
): AfeStatus | undefined {
  if (!raw) return undefined;
  switch (raw.trim().toLowerCase()) {
    case "draft":
      return "draft";
    case "submitted":
      return "submitted";
    case "approved":
      return "approved";
    default:
      return "draft";
  }
}

/** Normalize one AFE register record. Pure + idempotent: re-running on an already-migrated record
 *  returns an equal record (currency/status already present are preserved). */
export function migrateAfe(raw: Afe): Afe {
  const status = raw.status ?? mapLegacyAfeStatus(raw.afeStatus);
  // Trim-and-fall-through: a whitespace-only name is as unusable as an absent one, and a record
  // with neither name nor number still has to render as SOMETHING a user can click.
  const name = raw.name?.trim() || raw.afeNumber?.trim() || `AFE ${raw.id}`;
  return {
    ...raw,
    name,
    currency: raw.currency ?? "USD",
    ...(status ? { status } : {}),
    // afeClass intentionally NOT defaulted — unclassified-until-set (PO 2026-07-24).
  };
}
